import { matterDetailQueryKey } from "@/lib/data/static/matter-query-key";
import {
  invalidateSearchQueries,
  isCanonicalSearchQuery,
} from "@/lib/data/react-query/search/invalidation";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "../../static/query-keys";

import {
  createMedicalTreatmentBillV3,
  deleteMedicalTreatmentBillV1,
  updateMedicalTreatmentBillV1,
} from "@/lib/data/requests/matters/medical-treatment-bill";
import { type APIError, type APIResponse, type Matter } from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import { type AxiosError } from "axios";
import toast from "react-hot-toast";

export const useCreateMedicalTreatmentBill = ({
  suppressSuccessToast = false,
  suppressQueryInvalidation = false,
}: {
  suppressSuccessToast?: boolean;
  suppressQueryInvalidation?: boolean;
}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof createMedicalTreatmentBillV3>[0]) =>
      createMedicalTreatmentBillV3(data),
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        matterDetailQueryKey(data.matterId),
        (oldData: APIResponse<Matter> | undefined) => {
          if (!oldData) return;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              medicalTreatmentBills: [
                ...oldData.data.medicalTreatmentBills,
                {
                  ...data,
                  id: "optimistic",
                },
              ],
            },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData(
          matterDetailQueryKey(data.matterId),
        ),
      };
    },
    onError: (error: AxiosError<APIError>, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          matterDetailQueryKey(variables.matterId),
          context.previousData,
        );
      }
      toastAPIErrorMessage(error, "Error creating medical treatment bill");
    },
    onSettled: async () => {
      if (!suppressQueryInvalidation) {
        await invalidateSearchQueries(queryClient);
        await queryClient.invalidateQueries({
          predicate: (query) => !isCanonicalSearchQuery(query),
          queryKey: [queryKeys.matters],
        });
      }
    },
    onSuccess: () => {
      if (!suppressSuccessToast) {
        toast.success("Medical treatment bill created successfully");
      }
    },
  });
};

export const useUpdateMedicalTreatmentBill = ({
  suppressSuccessToast = false,
  suppressQueryInvalidation = false,
}: {
  suppressSuccessToast?: boolean;
  suppressQueryInvalidation?: boolean;
}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof updateMedicalTreatmentBillV1>[0]) =>
      updateMedicalTreatmentBillV1(data),
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        matterDetailQueryKey(data.matterId),
        (oldData: APIResponse<Matter> | undefined) => {
          if (!oldData) return;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              medicalTreatmentBills: [
                ...oldData.data.medicalTreatmentBills.map((bill) =>
                  bill.id === data.id ? { ...bill, ...data } : bill,
                ),
              ],
            },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData(
          matterDetailQueryKey(data.matterId),
        ),
      };
    },
    onError: (error: AxiosError<APIError>, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          matterDetailQueryKey(variables.matterId),
          context.previousData,
        );
      }
      toastAPIErrorMessage(error, "Error updating medical treatment bill");
    },
    onSettled: async () => {
      if (!suppressQueryInvalidation) {
        await invalidateSearchQueries(queryClient);
        await queryClient.invalidateQueries({
          predicate: (query) => !isCanonicalSearchQuery(query),
          queryKey: [queryKeys.matters],
        });
      }
    },
    onSuccess: () => {
      if (!suppressSuccessToast) {
        toast.success("Medical treatment bill updated successfully");
      }
    },
  });
};

export const useDeleteMedicalTreatmentBill = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof deleteMedicalTreatmentBillV1>[0]) =>
      deleteMedicalTreatmentBillV1(data),
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        matterDetailQueryKey(data.matterId),
        (oldData: APIResponse<Matter> | undefined) => {
          if (!oldData) return;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              medicalTreatmentBills: oldData.data.medicalTreatmentBills.filter(
                (bill) => bill.id !== data.id,
              ),
            },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData(
          matterDetailQueryKey(data.matterId),
        ),
      };
    },
    onError: (error: AxiosError<APIError>, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          matterDetailQueryKey(variables.matterId),
          context.previousData,
        );
      }
      toastAPIErrorMessage(error, "Error deleting medical treatment bill");
    },
    onSettled: async () => {
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matters],
      });
    },
    onSuccess: () => {
      toast.success("Medical treatment bill deleted successfully");
    },
  });
};
